import React from 'react';
import {
  FormGroup,
  ToggleSwitch,
  FormInput,
  Slider,
  StyleButtons,
  NavButtons,
  AdvancedColorInput,
  ChevronUpIcon,
  ChevronDownIcon,
} from '../common';

const CONTACT_METHODS = [
  { key: 'email', label: 'Email', showKey: 'showEmail' },
  { key: 'phone', label: 'Phone', showKey: 'showPhone' },
  { key: 'website', label: 'Website', showKey: 'showWebsite' },
];

const ContactsSection = ({ settings, onUpdate, onNavigate }) => {
  const handleSocialLinkChange = (platform, value) => {
    const newLinks = { ...settings.socialLinks, [platform]: value };
    onUpdate('socialLinks', newLinks);
  };

  return (
    <>
      <FormGroup title="Signature Options">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ToggleSwitch
            label="Show 'c./o. K.I.T. Group GmbH'"
            checked={settings.includeCoName}
            onChange={v => onUpdate('includeCoName', v)}
          />
          <ToggleSwitch
            label="Show 'Association Management'"
            checked={settings.includeAssociation}
            onChange={v => onUpdate('includeAssociation', v)}
          />
          <ToggleSwitch
            label="Show KIT Group Address"
            checked={settings.includeAddress}
            onChange={v => onUpdate('includeAddress', v)}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <FormInput
            label="Organizing Team Name"
            value={settings.organizingTeamName}
            onChange={e => onUpdate('organizingTeamName', e.target.value)}
            placeholder="e.g., The Event Team"
          />
          <Slider
            label="Font Size"
            value={settings.teamNameFontSize}
            onChange={v => onUpdate('teamNameFontSize', v)}
            min={8}
            max={24}
            unit="pt"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Style
            </label>
            <StyleButtons
              styles={settings.teamNameStyles}
              onToggle={v => onUpdate('teamNameStyles', v)}
              targetId="teamName"
            />
          </div>
        </div>
      </FormGroup>
      <FormGroup title="Contact Methods">
        <div className="p-4 bg-gray-50/80 rounded-lg border border-gray-200/80">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ToggleSwitch
              label="Show Email"
              checked={settings.showEmail}
              onChange={v => onUpdate('showEmail', v)}
            />
            <ToggleSwitch
              label="Show Phone"
              checked={settings.showPhone}
              onChange={v => onUpdate('showPhone', v)}
            />
            <ToggleSwitch
              label="Show Website"
              checked={settings.showWebsite}
              onChange={v => onUpdate('showWebsite', v)}
            />
          </div>
          <div className="mt-6 space-y-3">
            <p className="text-sm text-gray-600 font-medium">
              Contact Method Order
            </p>
            {(() => {
              const baseOrder = CONTACT_METHODS.map(m => m.key);
              const currentOrder = Array.isArray(settings.contactOrder)
                ? settings.contactOrder.filter(key => baseOrder.includes(key))
                : [];
              const normalizedOrder = [
                ...currentOrder,
                ...baseOrder.filter(key => !currentOrder.includes(key)),
              ];

              const move = (index, direction) => {
                const newIndex = direction === 'up' ? index - 1 : index + 1;
                if (newIndex < 0 || newIndex >= normalizedOrder.length) return;
                const next = [...normalizedOrder];
                [next[index], next[newIndex]] = [next[newIndex], next[index]];
                onUpdate('contactOrder', next);
              };

              return normalizedOrder.map((key, index) => {
                const method = CONTACT_METHODS.find(m => m.key === key);
                if (!method) return null;
                const isVisible = settings[method.showKey];
                return (
                  <div
                    key={method.key}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">
                        {method.label}
                      </span>
                      {!isVisible && (
                        <span className="text-xs text-gray-500">(hidden)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => move(index, 'up')}
                        disabled={index === 0}
                        className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label={`Move ${method.label} up`}
                      >
                        <ChevronUpIcon />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(index, 'down')}
                        disabled={index === normalizedOrder.length - 1}
                        className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label={`Move ${method.label} down`}
                      >
                        <ChevronDownIcon />
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
            <p className="text-xs text-gray-500">
              Use the arrows to choose the order contact methods appear in the email.
            </p>
          </div>
          {(settings.showEmail ||
            settings.showPhone ||
            settings.showWebsite) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4 border-t border-gray-200/80 pt-4">
              {settings.showEmail && (
                <FormInput
                  label="Contact Email"
                  value={settings.contactEmail}
                  onChange={e => onUpdate('contactEmail', e.target.value)}
                  onPaste={true}
                  placeholder="info@example.com"
                />
              )}
              {settings.showPhone && (
                <FormInput
                  label="Contact Phone"
                  value={settings.contactPhone}
                  onChange={e => onUpdate('contactPhone', e.target.value)}
                  onPaste={true}
                  placeholder="+1 234 567 890"
                />
              )}
              {settings.showWebsite && (
                <div className="space-y-3">
                  <FormInput
                    label="Website URL"
                    value={settings.websiteUrl}
                    onChange={e => onUpdate('websiteUrl', e.target.value)}
                    onPaste={true}
                    placeholder="https://www.example.com/path"
                    helpText="Enter the full destination with or without https:// (e.g., https://www.domain.com/path or www.domain.com/path). We'll add https:// automatically if it's missing."
                  />
                  <FormInput
                    label="Link Text"
                    value={settings.websiteDisplayText}
                    onChange={e =>
                      onUpdate('websiteDisplayText', e.target.value)
                    }
                    onPaste={true}
                    placeholder="www.example.com"
                    helpText="Recipients see this text while the link still opens the URL above."
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </FormGroup>
      <FormGroup title="Social Media">
        <ToggleSwitch
          label="Show Social Media Links"
          checked={settings.showSocialMedia}
          onChange={v => onUpdate('showSocialMedia', v)}
        />
        {settings.showSocialMedia && (
          <div className="p-4 border rounded-lg bg-gray-50/80 space-y-4">
            <FormInput
              label="Social Media Header"
              value={settings.socialTextInput}
              onChange={e => onUpdate('socialTextInput', e.target.value)}
              placeholder="Follow us"
            />
            <AdvancedColorInput
              label="Box Background Color"
              value={settings.socialMediaBackgroundColor}
              onChange={v => onUpdate('socialMediaBackgroundColor', v)}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Slider
                label="Icon Size"
                value={settings.socialIconSize || 24}
                onChange={v => onUpdate('socialIconSize', v)}
                min={16}
                max={48}
                unit="px"
              />
              <Slider
                label="Icon Border Radius"
                value={settings.socialIconBorderRadius || 0}
                onChange={v => onUpdate('socialIconBorderRadius', v)}
                min={0}
                max={12}
                unit="px"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Facebook URL"
                value={settings.socialLinks?.facebookUrl || ''}
                onChange={e =>
                  handleSocialLinkChange('facebookUrl', e.target.value)
                }
                onPaste={true}
              />
              <FormInput
                label="X (Twitter) URL"
                value={settings.socialLinks?.twitterUrl || ''}
                onChange={e =>
                  handleSocialLinkChange('twitterUrl', e.target.value)
                }
                onPaste={true}
              />
              <FormInput
                label="LinkedIn URL"
                value={settings.socialLinks?.linkedinUrl || ''}
                onChange={e =>
                  handleSocialLinkChange('linkedinUrl', e.target.value)
                }
                onPaste={true}
              />
              <FormInput
                label="Instagram URL"
                value={settings.socialLinks?.instagramUrl || ''}
                onChange={e =>
                  handleSocialLinkChange('instagramUrl', e.target.value)
                }
                onPaste={true}
              />
              <FormInput
                label="YouTube URL"
                value={settings.socialLinks?.youtubeUrl || ''}
                onChange={e =>
                  handleSocialLinkChange('youtubeUrl', e.target.value)
                }
                onPaste={true}
              />
              <FormInput
                label="Miscellaneous URL"
                value={settings.socialLinks?.miscUrl || ''}
                onChange={e =>
                  handleSocialLinkChange('miscUrl', e.target.value)
                }
                onPaste={true}
                placeholder="https://example.com/other"
              />
            </div>
          </div>
        )}
      </FormGroup>
      <NavButtons
        onPrev={() => onNavigate('images')}
        onNext={() => onNavigate('accessibility')}
        prevLabel="Previous: Layout & Media"
        nextLabel="Next: Accessibility Checker"
      />
    </>
  );
};
export default ContactsSection;
